import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Camera, UserPlus, Building2 } from "@/components/icons";
import { useOfficeFilter } from "../../lib/officeFilter";
import DatePicker from "../../components/DatePicker";
import AddressAutocomplete from "../../components/AddressAutocomplete";

const empty = {
  full_name: "",
  document_id: "",
  birth_date: "",
  gender: "",
  phone: "",
  email: "",
  address: "",
  blood_type: "",
  allergies: "",
  notes: "",
  photo_url: "",
};

export default function NewPatient({ rolePath = "doctor" }) {
  const navigate = useNavigate();
  const { officeId } = useOfficeFilter();
  const [offices, setOffices] = useState([]);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  // Load offices to display name
  useEffect(() => {
    api
      .get("/offices")
      .then((r) => setOffices(r.data))
      .catch(() => {});
  }, []);

  const activeOfficeName =
    offices.find((o) => o.id === officeId)?.name || null;

  const set = (k, v) => setForm({ ...form, [k]: v });

  const onPickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 800_000) {
      toast.error("La imagen es muy grande (máx 800KB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set("photo_url", reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    setSaving(true);
    try {
      // Si no hay filtro de oficina activo pero el doctor tiene UNA sola
      // oficina, asignarla automáticamente para no crear pacientes "huérfanos".
      const resolvedOfficeId =
        officeId || (offices.length === 1 ? offices[0].id : null);
      await api.post("/patients", { ...form, office_id: resolvedOfficeId });
      toast.success(
        activeOfficeName
          ? `Paciente registrado en ${activeOfficeName}`
          : "Paciente registrado"
      );
      navigate(`/${rolePath}/pacientes`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 fade-up" data-testid="new-patient-page">
      <button
        onClick={() => navigate(`/${rolePath}/pacientes`)}
        className="inline-flex items-center gap-2 text-[#1B405B] font-semibold text-sm hover:underline"
        data-testid="back-to-patients"
      >
        <ArrowLeft className="w-4 h-4" /> Volver a pacientes
      </button>

      <div>
        <h1 className="text-3xl font-extrabold text-[#163144] tracking-tight">
          Nuevo paciente
        </h1>
        <p className="text-sm text-[#1B405B]/70">
          Completa los datos clínicos para crear el expediente.
        </p>
      </div>

      {activeOfficeName && (
        <div
          className="glass rounded-[28px] px-4 py-3 flex items-center gap-3"
          data-testid="new-patient-office-banner"
        >
          <div className="w-9 h-9 rounded-xl bg-black/[0.06] flex items-center justify-center flex-shrink-0">
            <Building2 className="w-4 h-4 text-black" />
          </div>
          <div className="min-w-0 flex-1 text-sm">
            <span className="text-[#1B405B]/65">El paciente quedará asignado a la oficina </span>
            <span className="font-bold text-[#163144]">{activeOfficeName}</span>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="grid lg:grid-cols-[260px_1fr] gap-5">
        {/* Photo / identity */}
        <section className="glass rounded-[28px] p-6 self-start">
          <div className="flex flex-col items-center">
            <div className="relative">
              {form.photo_url ? (
                <img
                  src={form.photo_url}
                  alt="avatar"
                  className="w-28 h-28 rounded-2xl object-cover border-2 border-white shadow"
                />
              ) : (
                <div className="w-28 h-28 rounded-2xl bg-black/[0.06] flex items-center justify-center text-4xl font-extrabold text-black">
                  {form.full_name?.[0]?.toUpperCase() || "?"}
                </div>
              )}
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                title="Subir foto"
                data-testid="new-patient-photo-button"
                className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-[#15151A] text-white flex items-center justify-center shadow"
              >
                <Camera className="w-4 h-4" />
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPickPhoto}
                data-testid="new-patient-photo-file"
              />
            </div>
            <div className="mt-3 text-xs text-[#1B405B]/65 text-center">
              Foto del paciente (opcional, máx 800KB)
            </div>
          </div>
        </section>

        {/* Fields */}
        <section className="glass rounded-[28px] p-6 space-y-5">
          <h4 className="font-extrabold text-[#163144] flex items-center gap-2">
            <UserPlus className="w-4 h-4" /> Datos personales
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Nombre completo *">
              <input
                required
                value={form.full_name}
                onChange={(e) => set("full_name", e.target.value)}
                placeholder="Ej: María González"
                className="w-full glass-input rounded-xl px-4 py-2.5"
                data-testid="patient-full-name"
              />
            </Field>
            <Field label="Documento de identidad">
              <input
                value={form.document_id}
                onChange={(e) => set("document_id", e.target.value)}
                className="w-full glass-input rounded-xl px-4 py-2.5"
                data-testid="patient-document"
              />
            </Field>
            <Field label="Fecha de nacimiento">
              <DatePicker
                value={form.birth_date}
                onChange={(v) => set("birth_date", v)}
                testid="newpatient-birthdate"
              />
            </Field>
            <Field label="Teléfono">
              <input
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="809 555 1234"
                className="w-full glass-input rounded-xl px-4 py-2.5"
                data-testid="patient-phone"
              />
            </Field>
            <Field label="Correo electrónico">
              <input
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="paciente@correo.com"
                className="w-full glass-input rounded-xl px-4 py-2.5"
              />
            </Field>
            <Field label="Alergias">
              <input
                value={form.allergies}
                onChange={(e) => set("allergies", e.target.value)}
                placeholder="Ninguna, penicilina, etc."
                className="w-full glass-input rounded-xl px-4 py-2.5"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Género">
              <PillGroup
                testid="patient-gender"
                options={[
                  { value: "male", label: "Masculino" },
                  { value: "female", label: "Femenino" },
                  { value: "other", label: "Otro" },
                ]}
                value={form.gender}
                onChange={(v) => set("gender", v)}
              />
            </Field>

            <Field label="Tipo de sangre">
              <PillGroup
                testid="patient-blood-type"
                options={["O+", "O-", "A+", "A-", "B+", "B-", "AB+", "AB-"].map(
                  (b) => ({ value: b, label: b })
                )}
                value={form.blood_type}
                onChange={(v) => set("blood_type", v)}
              />
            </Field>
          </div>

          <Field label="Dirección">
            <AddressAutocomplete
              value={form.address}
              onChange={(v) => set("address", v)}
              showIcon={false}
              inputClassName="w-full glass-input rounded-xl px-4 py-2.5"
              testid="patient-address"
            />
          </Field>

          <Field label="Notas">
            <textarea
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Observaciones generales, antecedentes…"
              className="w-full glass-input rounded-xl px-4 py-2.5 min-h-[90px] text-sm"
            />
          </Field>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => navigate(`/${rolePath}/pacientes`)}
              className="px-6 py-2.5 rounded-full font-bold text-[#1B405B] hover:bg-white/55"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="patient-submit-button"
              className="btn-primary"
            >
              {saving ? "Guardando..." : "Registrar paciente"}
            </button>
          </div>
        </section>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="block">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#1B405B]/65">
        {label}
      </div>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function PillGroup({ options, value, onChange, testid }) {
  return (
    <div className="inline-flex flex-wrap gap-1.5 bg-white/40 rounded-full p-1" data-testid={testid}>
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(selected ? "" : opt.value)}
            data-testid={`${testid}-${opt.value}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition ${
              selected
                ? "bg-[#15151A] text-white shadow"
                : "text-[#1B405B]/75 hover:bg-white/60"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
